import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../../components/Screen/Screen';
import { ScreenHeader } from '../../components/ScreenHeader/ScreenHeader';
import { Button } from '../../components/Button/Button';
import { copy, labels } from '../../constants';
import { useOnboardingStore } from '../../store/onboarding';
import { OnboardingAPI } from '../../api/onboarding';
import './Review.css';

export const Review: React.FC = () => {
  const navigate = useNavigate();
  const { data, resetOnboarding } = useOnboardingStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const onboardingData: any = {
        ...data,
        // Ensure province has a sensible default in case persisted state was empty
        province: data.province || 'AB',
        timestamp: new Date().toISOString()
      };
      
      // Submit to API via OnboardingAPI (uses VITE_API_BASE_URL or localhost:8787)
      await OnboardingAPI.complete(Math.random().toString(36), onboardingData);
      
      navigate('/confirmation');
      resetOnboarding();
    } catch (error) {
      console.error('Submission error:', error);
      setIsSubmitting(false);
      alert('There was a problem submitting your request. Please try again.');
    }
  };

  const getSummaryItem = (label: string, value: string | string[] | null | undefined) => {
    if (!value) return null;
    
    const displayValue = Array.isArray(value) ? value.join(', ') : value;
    
    return (
      <div className="review-item">
        <div className="review-item__label">{label}</div>
        <div className="review-item__value">{displayValue}</div>
      </div>
    );
  };

  return (
    <Screen>
      <ScreenHeader
        title={copy.review.title}
        subtitle={copy.review.subtitle}
      />
      
      <div className="review-summary">
        {getSummaryItem(
          copy.review.sections.who,
          data.for_whom ? labels.forWhom[data.for_whom] : null
        )}
        
        {getSummaryItem(
          copy.review.sections.help,
          data.help_type ? labels.helpType[data.help_type] : null
        )}
        
        {getSummaryItem(
          copy.review.sections.concerns,
          data.concerns?.map(c => labels.concerns[c])
        )}
        
        {data.optional_note && getSummaryItem(
          copy.review.sections.note,
          data.optional_note
        )}
        
        {getSummaryItem(
          copy.review.sections.occupation,
          data.occupation_category 
            ? `${labels.occupationCategory[data.occupation_category]}${
                data.occupation_detail 
                  ? ` - ${labels.firstResponderRole[data.occupation_detail]}` 
                  : ''
              }`
            : null
        )}
        
        {getSummaryItem(
          copy.review.sections.location,
          data.province 
            ? `${labels.provinces[data.province]}${data.city ? `, ${data.city}` : ''}`
            : null
        )}
        
        {getSummaryItem(
          copy.review.sections.contact,
          `${data.name ? `${data.name} · ` : ''}${data.email}${data.phone ? ` · ${data.phone}` : ''}`
        )}
      </div>
      
      <div style={{ marginTop: 'var(--spacing-lg)' }}>
        <Button
          variant="primary"
          size="large"
          fullWidth
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : copy.review.button}
        </Button>
      </div>
    </Screen>
  );
};
